import { MAINTAINER_PATHS } from '@liquid-spirit/api/domains/maintainer';
import { getWebVitalSnapshotKey, sameMetricSnapshot } from './metric-snapshots.js';
import { hasBrowserDocument, subscribeWebVitals } from './web-vitals.js';

export { getWebVitalSnapshotKey, sameMetricSnapshot } from './metric-snapshots.js';

export const RUM_SCHEMA_VERSION = 2;
export const DEFAULT_RUM_PATH = MAINTAINER_PATHS.rumMetrics;

const DEFAULT_FLUSH_DELAY_MS = 5000;
const DEFAULT_MAX_BATCH_SIZE = 100;
const ID_SEGMENT = /^(?:\d+|[^/\s]+@[^/\s]+|[0-9a-f]{16,}|[0-9a-f]{8}-[0-9a-f-]{27,})$/i;

const defaultClock = () => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
};

const createId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const trimSlashes = (value = '') => String(value || '').trim().replace(/\/+$/, '');

export const resolveRumEndpoint = (baseUrl = '', endpoint = DEFAULT_RUM_PATH) => {
  if (/^https?:\/\//i.test(endpoint)) return endpoint;
  const base = trimSlashes(baseUrl);
  const path = `/${String(endpoint || DEFAULT_RUM_PATH).replace(/^\/+/, '')}`;
  if (!base) return path;
  if (base.endsWith('/api') && path.startsWith('/api/')) {
    return `${base}${path.slice(4)}`;
  }
  return `${base}${path}`;
};

export const sanitizeMetricRoute = (value = '/') => {
  const raw = String(value || '/').split(/[?#]/, 1)[0] || '/';
  const normalized = raw.startsWith('/') ? raw : `/${raw}`;
  const sanitized = normalized
    .split('/')
    .map((segment) => (ID_SEGMENT.test(segment) ? ':id' : segment.slice(0, 80)))
    .join('/');
  return sanitized.slice(0, 256) || '/';
};

const resolveDeviceClass = () => {
  if (typeof window === 'undefined') return 'mobile';
  const width = Number(window.innerWidth || 0);
  if (width <= 640) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
};

const resolveNetworkType = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return connection?.effectiveType || 'unknown';
};

/** Browser-only lazy official metrics; cleanup unsubscribes without finalizing. */
export const observeWebVitals = ({ record, getContext, onReport } = {}) => subscribeWebVitals({
  record, getContext, onReport, sanitizeRoute: sanitizeMetricRoute,
});

export const createMetricsReporter = ({
  application,
  platform = 'web',
  baseUrl = '',
  endpoint = DEFAULT_RUM_PATH,
  release = '',
  deployment = '',
  enabled = true,
  fetchImpl = typeof globalThis.fetch === 'function' ? globalThis.fetch.bind(globalThis) : null,
  flushDelayMs = DEFAULT_FLUSH_DELAY_MS,
  maxBatchSize = DEFAULT_MAX_BATCH_SIZE,
  clock = defaultClock,
  routeProvider = () => (
    typeof window !== 'undefined' && window.location?.pathname
      ? window.location.pathname
      : '/'
  ),
  deviceClass = resolveDeviceClass(),
  networkTypeProvider = resolveNetworkType,
} = {}) => {
  if (!application || typeof application !== 'string') {
    throw new TypeError('createMetricsReporter requires an application identifier');
  }

  const resolvedEndpoint = resolveRumEndpoint(baseUrl, endpoint);
  const batchSize = Number.isSafeInteger(maxBatchSize) && maxBatchSize > 0
    ? maxBatchSize : DEFAULT_MAX_BATCH_SIZE;
  const sessionId = createId();
  const startedAt = platform === 'web' ? 0 : clock();
  let sequence = 0;
  let initialized = false;
  let interactiveRecorded = false;
  let flushTimer = null;
  let flushPromise = null;
  let consecutiveFailures = 0;
  let queue = [];
  let stopVitals = null;
  let stopLifecycleFlush = null;
  let destroyed = false;
  let hiddenFlushRequested = null;
  const snapshots = new Map();
  const pendingPaints = new Set();
  const queueLimit = batchSize * 3;

  const scheduleFlush = () => {
    if (!enabled || destroyed || flushTimer || !queue.length || typeof setTimeout !== 'function') return;
    const retryMultiplier = 2 ** Math.min(consecutiveFailures, 5);
    flushTimer = setTimeout(() => {
      flushTimer = null;
      void flush();
    }, flushDelayMs * retryMultiplier);
  };

  const record = ({ metric, value, unit = 'ms', route, journey = 'other', metadata = {} } = {}) => {
    const numericValue = Number(value);
    if (!enabled || destroyed || !metric || !Number.isFinite(numericValue)) return null;
    const snapshotKey = getWebVitalSnapshotKey({ metric, metadata });
    const previous = snapshotKey ? snapshots.get(snapshotKey) : null;
    if (previous && metadata.snapshotRevision <= previous.metadata.snapshotRevision) return null;
    if (!previous) sequence += 1;
    const sample = {
      id: previous?.id || `${application}:${sessionId}:${sequence}`,
      application,
      platform,
      release: String(release || '').slice(0, 120),
      metric: String(metric).slice(0, 64),
      value: numericValue,
      unit: String(unit || '').slice(0, 32),
      route: previous?.route ?? sanitizeMetricRoute(route || routeProvider()),
      journey: previous?.journey ?? String(journey || 'other').slice(0, 64),
      deviceClass,
      networkType: networkTypeProvider(),
      metadata: { ...metadata },
      timestamp: previous?.timestamp || new Date().toISOString(),
    };
    const queuedIndex = previous ? queue.findIndex((item) => item.id === previous.id) : -1;
    if (queuedIndex >= 0) queue[queuedIndex] = sample;
    else queue.push(sample);
    // Retain the existing bounded offline queue policy. Snapshot races do not
    // lose retained records; prolonged offline overflow can still evict oldest.
    if (queue.length > queueLimit) queue = queue.slice(-queueLimit);
    if (snapshotKey) {
      snapshots.delete(snapshotKey);
      snapshots.set(snapshotKey, sample);
      if (snapshots.size > Math.max(5, queueLimit)) snapshots.delete(snapshots.keys().next().value);
    }
    scheduleFlush();
    return sample;
  };

  function flush(options) {
    return flushCaptured(options);
  }

  async function flushCaptured({ useBeacon = false } = {}, requestedSnapshots) {
    if (destroyed) return false;
    if (!enabled || !queue.length) return true;
    if (flushPromise && !useBeacon) return flushPromise;
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }

    // A hidden flush owns only the finite snapshot captured by that call. It
    // never chases records appended by a transport callback or future event.
    const captured = requestedSnapshots || (useBeacon ? queue.slice() : queue.slice(0, batchSize));
    const retained = (samples) => samples.filter((sample) => (
      queue.some((current) => sameMetricSnapshot(current, sample))
    ));
    const encode = (samples) => JSON.stringify({
      schemaVersion: RUM_SCHEMA_VERSION,
      sentAt: new Date().toISOString(),
      application,
      platform,
      release: String(release || '').slice(0, 120),
      deployment: String(deployment || '').slice(0, 64),
      samples,
    });
    const acknowledge = (pending) => {
      queue = queue.filter((sample) => !pending.some((sent) => sameMetricSnapshot(sample, sent)));
    };

    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      for (let offset = 0; offset < captured.length; offset += batchSize) {
        if (destroyed) return false;
        const batch = retained(captured.slice(offset, offset + batchSize));
        if (!batch.length) continue;
        try {
          const sent = navigator.sendBeacon(
            resolvedEndpoint,
            new Blob([encode(batch)], { type: 'application/json' }),
          );
          if (!sent) break;
          acknowledge(batch);
        } catch {
          // Stop at the first rejected batch and fall back to fetch.
          break;
        }
      }
    }

    const remaining = retained(captured);
    if (!remaining.length) {
      consecutiveFailures = 0;
      if (queue.length) scheduleFlush();
      return true;
    }
    if (flushPromise) {
      // The latest hidden request captures all retained current revisions.
      // Its continuation is bounded by this set, rather than a fresh queue.
      hiddenFlushRequested = remaining;
      return flushPromise;
    }
    if (!fetchImpl) return false;
    let completed = false;
    flushPromise = Promise.resolve().then(async () => {
      try {
        for (let offset = 0; offset < remaining.length; offset += batchSize) {
          if (destroyed) return false;
          const pending = retained(remaining.slice(offset, offset + batchSize));
          if (!pending.length) continue;
          const response = await fetchImpl(resolvedEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: encode(pending),
            keepalive: platform === 'web',
          });
          if (!response?.ok) {
            consecutiveFailures += 1;
            return false;
          }
          acknowledge(pending);
        }
        consecutiveFailures = 0;
        completed = true;
        return true;
      } catch {
        consecutiveFailures += 1;
        return false;
      } finally {
        flushPromise = null;
        const hiddenSnapshots = hiddenFlushRequested;
        hiddenFlushRequested = null;
        if (!destroyed && queue.length) {
          // Stop a failed hidden drain immediately; retained data retries on
          // the bounded backoff timer. A pending hide during an ordinary
          // upload may still start its one separately requested drain.
          if (hiddenSnapshots && (completed || !useBeacon)) {
            void flushCaptured({ useBeacon: true }, hiddenSnapshots);
          } else scheduleFlush();
        }
      }
    });
    return flushPromise;
  }

  const markInteractive = ({ route, journey = 'other', metadata = {} } = {}) => {
    if (destroyed || interactiveRecorded) return null;
    interactiveRecorded = true;
    return record({
      metric: 'boot_time',
      value: Math.max(0, clock() - startedAt),
      unit: 'ms',
      route,
      journey,
      metadata,
    });
  };

  const markInteractiveAfterPaint = (input = {}) => {
    if (destroyed || !enabled || typeof requestAnimationFrame !== 'function') return () => {};
    let cancelled = false;
    let secondFrame = null;
    const firstFrame = requestAnimationFrame(() => {
      if (destroyed || cancelled) return;
      secondFrame = requestAnimationFrame(() => {
        pendingPaints.delete(cancel);
        if (destroyed || cancelled) return;
        markInteractive({
          ...input,
          metadata: { milestone: 'second-animation-frame-after-root-render', ...(input.metadata || {}) },
        });
      });
    });
    const cancel = () => {
      cancelled = true;
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) cancelAnimationFrame(secondFrame);
      pendingPaints.delete(cancel);
    };
    pendingPaints.add(cancel);
    return cancel;
  };

  const initialize = ({ observeVitals = platform === 'web' } = {}) => {
    if (!enabled || initialized || destroyed) return;
    initialized = true;
    record({
      metric: 'app_session',
      value: 1,
      unit: 'count',
      journey: `${application}:session`,
      metadata: { deployment: String(deployment || '').slice(0, 64) },
    });

    if (platform === 'web' && hasBrowserDocument()) {
      const flushWhenHidden = () => {
        if (document.visibilityState === 'hidden') void flush({ useBeacon: true });
      };
      if (observeVitals) stopVitals = observeWebVitals({ record, onReport: flushWhenHidden });
      const flushOnPageHide = () => { void flush({ useBeacon: true }); };
      document.addEventListener('visibilitychange', flushWhenHidden);
      window.addEventListener('pagehide', flushOnPageHide);
      stopLifecycleFlush = () => {
        document.removeEventListener('visibilitychange', flushWhenHidden);
        window.removeEventListener('pagehide', flushOnPageHide);
      };
    }
  };

  const destroy = () => {
    destroyed = true;
    stopVitals?.();
    stopVitals = null;
    stopLifecycleFlush?.();
    stopLifecycleFlush = null;
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = null;
    hiddenFlushRequested = null;
    pendingPaints.forEach((cancel) => cancel());
    snapshots.clear();
    queue = [];
  };

  return {
    destroy,
    flush,
    initialize,
    markInteractive,
    markInteractiveAfterPaint,
    record,
    get pendingCount() { return queue.length; },
    endpoint: resolvedEndpoint,
  };
};
